import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { useLanguage } from '../../context/LanguageContext';
import { db } from '../../firebase/config';
import { ChevronDown, ChevronUp, Eye, FileText, Shield, Users, AlertTriangle, Scale, Globe, Clock, CheckCircle, ExternalLink, Download } from 'lucide-react';

const TermsOfService = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [scrollProgress, setScrollProgress] = useState(0);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [hasAccepted, setHasAccepted] = useState(false);
  const [hasReadSufficiently, setHasReadSufficiently] = useState(false);
  const [sessionTimeout, setSessionTimeout] = useState(false);
  const [showTooltip, setShowTooltip] = useState<string | null>(null);

  // Session timeout (30 minutes)
  useEffect(() => {
    const timer = setTimeout(() => {
      setSessionTimeout(true);
    }, 30 * 60 * 1000);

    return () => clearTimeout(timer);
  }, []);

  // Scroll progress tracking
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrollTop / docHeight) * 100;
      setScrollProgress(progress);
      
      // Enable acceptance when 90% read
      if (progress >= 90) {
        setHasReadSufficiently(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleSection = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const handleAcceptance = () => {
    if (hasReadSufficiently && !sessionTimeout) {
      setHasAccepted(true);
      // Store acceptance in localStorage with timestamp
      localStorage.setItem('opportunai_terms_accepted', JSON.stringify({
        accepted: true,
        timestamp: new Date().toISOString(),
        version: '2025-01-01'
      }));
    }
  };

  const handleAccept = async () => {
    try {
      await addDoc(collection(db, 'termsAcceptances'), {
        accepted: true,
        timestamp: serverTimestamp(),
        version: '2025-01-01',
      });
    } catch {
      // save best-effort; do not block navigation
    }
    navigate('/register');
  };

  const handleDecline = () => {
    navigate('/');
  };

  const printTerms = () => {
    window.print();
  };

  const isEnglish = language === 'en';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Progress Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm">
        <div className="h-1 bg-gray-200">
          <div 
            className="h-full bg-primary-600 transition-all duration-300"
            style={{ width: `${scrollProgress}%` }}
          />
        </div>
        <div className="container mx-auto px-4 py-2 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <h1 className="text-lg font-semibold text-gray-900">
              {isEnglish ? 'Terms of Service' : 'Conditions Générales d\'Utilisation'}
            </h1>
            <span className="text-sm text-gray-500">
              {Math.round(scrollProgress)}% {isEnglish ? 'read' : 'lu'}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={printTerms}
              className="flex items-center px-3 py-1 text-sm text-gray-600 hover:text-gray-800 transition-colors"
            >
              <Download className="h-4 w-4 mr-1" />
              {isEnglish ? 'Print' : 'Imprimer'}
            </button>
            {hasReadSufficiently && (
              <CheckCircle className="h-5 w-5 text-green-600" />
            )}
          </div>
        </div>
      </div>

      {/* Session Timeout Warning */}
      {sessionTimeout && (
        <div className="fixed top-16 left-0 right-0 z-40 bg-red-600 text-white p-3 text-center">
          <AlertTriangle className="h-5 w-5 inline mr-2" />
          {isEnglish 
            ? 'Session expired. Please refresh the page to continue.' 
            : 'Session expirée. Veuillez actualiser la page pour continuer.'
          }
        </div>
      )}

      <div className="container mx-auto px-4 py-24 pb-32 max-w-7xl">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              {isEnglish ? 'Terms of Service' : 'Conditions Générales d\'Utilisation'}
            </h1>
            <p className="text-lg text-gray-600 mb-4">
              Opportunai.com - {isEnglish ? 'AI Marketplace Platform' : 'Plateforme de Marché IA'}
            </p>
            <div className="flex justify-center items-center space-x-6 text-sm text-gray-500">
              <div className="flex items-center">
                <Clock className="h-4 w-4 mr-1" />
                {isEnglish ? 'Last Updated' : 'Dernière mise à jour'}: 01/01/2025
              </div>
              <div className="flex items-center">
                <Globe className="h-4 w-4 mr-1" />
                {isEnglish ? 'Version' : 'Version'}: 2.0
              </div>
              <div className="flex items-center">
                <Scale className="h-4 w-4 mr-1" />
                {isEnglish ? 'EU Law Compliant' : 'Conforme au droit UE'}
              </div>
            </div>
          </div>

          {/* Company Information */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
            <h2 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
              <Shield className="h-5 w-5 mr-2" />
              {isEnglish ? 'Service Provider Identity' : 'Identité du Prestataire'}
            </h2>
            <div className="grid md:grid-cols-2 gap-6 text-sm">
              <div>
                <p><strong>{isEnglish ? 'Company' : 'Société'}:</strong> KphApps</p>
                <p><strong>{isEnglish ? 'Legal Form' : 'Forme juridique'}:</strong> {isEnglish ? 'TBC' : 'N/A'}</p>
                <p><strong>SIRET:</strong> [À compléter lors de l'immatriculation]</p>
                <p><strong>{isEnglish ? 'Registered Office' : 'Siège social'}:</strong> [Adresse à compléter]</p>
              </div>
              <div>
                <p><strong>{isEnglish ? 'Data Controller' : 'Responsable de traitement'}:</strong> KphApps</p>
                <p><strong>DPO:</strong> contact@opportunai.com</p>
                <p><strong>{isEnglish ? 'EU Representative' : 'Représentant UE'}:</strong> [Si applicable]</p>
                <p><strong>{isEnglish ? 'Supervisory Authority' : 'Autorité de contrôle'}:</strong> CNIL (France)</p>
              </div>
            </div>
          </div>
        </div>

        {/* Two-column layout */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Section 1: Acceptance and Scope */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <button
                onClick={() => toggleSection('acceptance')}
                className="w-full p-6 text-left hover:bg-gray-50 transition-colors"
              >
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                    <FileText className="h-5 w-5 mr-2 text-primary-600" />
                    1. {isEnglish ? 'Acceptance and Scope' : 'Acceptation et Champ d\'Application'}
                  </h2>
                  {expandedSections.has('acceptance') ? 
                    <ChevronUp className="h-5 w-5 text-gray-500" /> : 
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  }
                </div>
              </button>
              
              {expandedSections.has('acceptance') && (
                <div className="px-6 pb-6 border-t border-gray-100">
                  <div className="prose max-w-none">
                    {isEnglish ? (
                      <>
                        <p className="mb-4">
                          These Terms of Service ("Terms") govern your access to and use of the OpportunAI platform, 
                          operated by Kphapps.
                        </p>
                        <p className="mb-4">
                          <strong>Legal Framework:</strong> These Terms are governed by:
                        </p>
                        <ul className="list-disc pl-6 mb-4">
                          <li>EU General Data Protection Regulation (GDPR) 2016/679</li>
                          <li>E-Commerce Directive 2000/31/EC</li>
                          <li>Consumer Rights Directive 2011/83/EU</li>
                          <li>Unfair Commercial Practices Directive 2005/29/EC</li>
                          <li>French Consumer Code and Civil Code</li>
                        </ul>
                        <p>
                          By creating an account, you acknowledge that you have read, understood, and agree to be bound by these Terms.
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="mb-4">
                          Les présentes Conditions Générales d'Utilisation ("CGU") régissent votre accès et utilisation 
                          de la plateforme OpportunAI, exploitée par KphApps.
                        </p>
                        <p className="mb-4">
                          <strong>Cadre juridique :</strong> Ces CGU sont régies par :
                        </p>
                        <ul className="list-disc pl-6 mb-4">
                          <li>Règlement Général sur la Protection des Données (RGPD) 2016/679</li>
                          <li>Directive Commerce Électronique 2000/31/CE</li>
                          <li>Directive Droits des Consommateurs 2011/83/UE</li>
                          <li>Directive Pratiques Commerciales Déloyales 2005/29/CE</li>
                          <li>Code de la consommation et Code civil français</li>
                        </ul>
                        <p>
                          En créant un compte, vous reconnaissez avoir lu, compris et accepté d'être lié par ces CGU.
                        </p>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Section 2: Data Processing */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <button
                onClick={() => toggleSection('data')}
                className="w-full p-6 text-left hover:bg-gray-50 transition-colors"
              >
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                    <Shield className="h-5 w-5 mr-2 text-primary-600" />
                    2. {isEnglish ? 'Personal Data Processing (GDPR)' : 'Traitement des Données Personnelles (RGPD)'}
                  </h2>
                  {expandedSections.has('data') ? 
                    <ChevronUp className="h-5 w-5 text-gray-500" /> : 
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  }
                </div>
              </button>
              
              {expandedSections.has('data') && (
                <div className="px-6 pb-6 border-t border-gray-100">
                  <div className="prose max-w-none">
                    {isEnglish ? (
                      <>
                        <h3 className="text-lg font-semibold mb-3">2.1 Legal Basis for Processing (Art. 6 GDPR)</h3>
                        <ul className="list-disc pl-6 mb-4">
                          <li><strong>Contract Performance:</strong> Processing necessary for service delivery</li>
                          <li><strong>Legitimate Interest:</strong> Platform security, fraud prevention, analytics</li>
                          <li><strong>Consent:</strong> Marketing communications, optional features</li>
                          <li><strong>Legal Obligation:</strong> Tax reporting, regulatory compliance</li>
                        </ul>

                        <h3 className="text-lg font-semibold mb-3">2.2 Data Categories Processed</h3>
                        <ul className="list-disc pl-6 mb-4">
                          <li>Identity data: Name, email, professional information</li>
                          <li>Profile data: Skills, company information, preferences</li>
                          <li>Communication data: Messages, proposals, support requests</li>
                          <li>Technical data: IP address, browser information, usage analytics</li>
                          <li>Financial data: Subscription information, payment history</li>
                        </ul>

                        <h3 className="text-lg font-semibold mb-3">2.3 Data Retention Periods</h3>
                        <ul className="list-disc pl-6 mb-4">
                          <li>Active accounts: Duration of service + 3 years</li>
                          <li>Inactive accounts: Deleted after 3 years of inactivity</li>
                          <li>Financial records: 10 years (legal obligation)</li>
                          <li>Communication logs: 1 year for security purposes</li>
                        </ul>

                        <h3 className="text-lg font-semibold mb-3">2.4 Your Rights Under GDPR</h3>
                        <ul className="list-disc pl-6 mb-4">
                          <li><strong>Access (Art. 15):</strong> Request copy of your personal data</li>
                          <li><strong>Rectification (Art. 16):</strong> Correct inaccurate data</li>
                          <li><strong>Erasure (Art. 17):</strong> "Right to be forgotten"</li>
                          <li><strong>Restriction (Art. 18):</strong> Limit processing in certain cases</li>
                          <li><strong>Portability (Art. 20):</strong> Receive data in structured format</li>
                          <li><strong>Objection (Art. 21):</strong> Object to processing based on legitimate interest</li>
                          <li><strong>Withdraw Consent:</strong> For consent-based processing</li>
                        </ul>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                          <p><strong>Exercise Your Rights:</strong> Contact contact@opportunai.com</p>
                          <p><strong>Response Time:</strong> Within 1 month (extendable to 3 months for complex requests)</p>
                          <p><strong>Complaint Right:</strong> You may lodge a complaint with CNIL (www.cnil.fr)</p>
                        </div>
                      </>
                    ) : (
                      <>
                        <h3 className="text-lg font-semibold mb-3">2.1 Base Juridique du Traitement (Art. 6 RGPD)</h3>
                        <ul className="list-disc pl-6 mb-4">
                          <li><strong>Exécution du contrat :</strong> Traitement nécessaire à la prestation de service</li>
                          <li><strong>Intérêt légitime :</strong> Sécurité de la plateforme, prévention de la fraude, analyses</li>
                          <li><strong>Consentement :</strong> Communications marketing, fonctionnalités optionnelles</li>
                          <li><strong>Obligation légale :</strong> Déclarations fiscales, conformité réglementaire</li>
                        </ul>

                        <h3 className="text-lg font-semibold mb-3">2.2 Catégories de Données Traitées</h3>
                        <ul className="list-disc pl-6 mb-4">
                          <li>Données d'identité : Nom, email, informations professionnelles</li>
                          <li>Données de profil : Compétences, informations d'entreprise, préférences</li>
                          <li>Données de communication : Messages, propositions, demandes de support</li>
                          <li>Données techniques : Adresse IP, informations navigateur, analyses d'usage</li>
                          <li>Données financières : Informations d'abonnement, historique de paiement</li>
                        </ul>

                        <h3 className="text-lg font-semibold mb-3">2.3 Durées de Conservation</h3>
                        <ul className="list-disc pl-6 mb-4">
                          <li>Comptes actifs : Durée du service + 3 ans</li>
                          <li>Comptes inactifs : Suppression après 3 ans d'inactivité</li>
                          <li>Documents financiers : 10 ans (obligation légale)</li>
                          <li>Journaux de communication : 1 an pour la sécurité</li>
                        </ul>

                        <h3 className="text-lg font-semibold mb-3">2.4 Vos Droits selon le RGPD</h3>
                        <ul className="list-disc pl-6 mb-4">
                          <li><strong>Accès (Art. 15) :</strong> Demander une copie de vos données personnelles</li>
                          <li><strong>Rectification (Art. 16) :</strong> Corriger les données inexactes</li>
                          <li><strong>Effacement (Art. 17) :</strong> "Droit à l'oubli"</li>
                          <li><strong>Limitation (Art. 18) :</strong> Limiter le traitement dans certains cas</li>
                          <li><strong>Portabilité (Art. 20) :</strong> Recevoir les données dans un format structuré</li>
                          <li><strong>Opposition (Art. 21) :</strong> S'opposer au traitement basé sur l'intérêt légitime</li>
                          <li><strong>Retrait du consentement :</strong> Pour les traitements basés sur le consentement</li>
                        </ul>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                          <p><strong>Exercer vos droits :</strong> Contactez contact@opportunai.com</p>
                          <p><strong>Délai de réponse :</strong> Dans le mois (extensible à 3 mois pour les demandes complexes)</p>
                          <p><strong>Droit de réclamation :</strong> Vous pouvez déposer une plainte auprès de la CNIL (www.cnil.fr)</p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Section 3: AI-Specific Provisions */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <button
                onClick={() => toggleSection('ai')}
                className="w-full p-6 text-left hover:bg-gray-50 transition-colors"
              >
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                    <Users className="h-5 w-5 mr-2 text-primary-600" />
                    3. {isEnglish ? 'AI and Automated Decision-Making' : 'IA et Prise de Décision Automatisée'}
                  </h2>
                  {expandedSections.has('ai') ? 
                    <ChevronUp className="h-5 w-5 text-gray-500" /> : 
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  }
                </div>
              </button>
              
              {expandedSections.has('ai') && (
                <div className="px-6 pb-6 border-t border-gray-100">
                  <div className="prose max-w-none">
                    {isEnglish ? (
                      <>
                        <h3 className="text-lg font-semibold mb-3">3.1 Automated Decision-Making (Art. 22 GDPR)</h3>
                        <p className="mb-4">
                          OpportunAI uses automated systems for:
                        </p>
                        <ul className="list-disc pl-6 mb-4">
                          <li>Project-provider matching algorithms</li>
                          <li>Content moderation and spam detection</li>
                          <li>Fraud prevention and security monitoring</li>
                          <li>Performance analytics and recommendations</li>
                        </ul>

                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                          <h4 className="font-semibold mb-2">Right to Human Intervention</h4>
                          <p>You have the right to:</p>
                          <ul className="list-disc pl-6 mt-2">
                            <li>Request human review of automated decisions</li>
                            <li>Express your point of view</li>
                            <li>Contest the decision</li>
                          </ul>
                          <p className="mt-2"><strong>Contact:</strong> ai-review@opportunai.com</p>
                        </div>

                        <h3 className="text-lg font-semibold mb-3">3.2 Algorithmic Transparency</h3>
                        <ul className="list-disc pl-6 mb-4">
                          <li><strong>Matching Algorithm:</strong> Based on skills compatibility, location, and project requirements</li>
                          <li><strong>Ranking Factors:</strong> Profile completeness, past performance, user ratings</li>
                          <li><strong>Training Data:</strong> Anonymized historical platform interactions</li>
                          <li><strong>Bias Mitigation:</strong> Regular audits for gender, age, and nationality bias</li>
                        </ul>

                        <h3 className="text-lg font-semibold mb-3">3.3 Prohibited AI Outcomes</h3>
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <p className="font-semibold mb-2">Strict Prohibition Against:</p>
                          <ul className="list-disc pl-6">
                            <li>Discriminatory matching based on protected characteristics</li>
                            <li>Unfair pricing based on personal attributes</li>
                            <li>Manipulation of user behavior for commercial gain</li>
                            <li>Profiling for sensitive personal data inference</li>
                          </ul>
                        </div>
                      </>
                    ) : (
                      <>
                        <h3 className="text-lg font-semibold mb-3">3.1 Prise de Décision Automatisée (Art. 22 RGPD)</h3>
                        <p className="mb-4">
                          OpportunAI utilise des systèmes automatisés pour :
                        </p>
                        <ul className="list-disc pl-6 mb-4">
                          <li>Algorithmes de mise en relation projet-prestataire</li>
                          <li>Modération de contenu et détection de spam</li>
                          <li>Prévention de la fraude et surveillance sécuritaire</li>
                          <li>Analyses de performance et recommandations</li>
                        </ul>

                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                          <h4 className="font-semibold mb-2">Droit à l'Intervention Humaine</h4>
                          <p>Vous avez le droit de :</p>
                          <ul className="list-disc pl-6 mt-2">
                            <li>Demander une révision humaine des décisions automatisées</li>
                            <li>Exprimer votre point de vue</li>
                            <li>Contester la décision</li>
                          </ul>
                          <p className="mt-2"><strong>Contact :</strong> ai-review@opportunai.com</p>
                        </div>

                        <h3 className="text-lg font-semibold mb-3">3.2 Transparence Algorithmique</h3>
                        <ul className="list-disc pl-6 mb-4">
                          <li><strong>Algorithme de mise en relation :</strong> Basé sur la compatibilité des compétences, localisation et exigences projet</li>
                          <li><strong>Facteurs de classement :</strong> Complétude du profil, performances passées, évaluations utilisateurs</li>
                          <li><strong>Données d'entraînement :</strong> Interactions historiques anonymisées de la plateforme</li>
                          <li><strong>Atténuation des biais :</strong> Audits réguliers pour les biais de genre, âge et nationalité</li>
                        </ul>

                        <h3 className="text-lg font-semibold mb-3">3.3 Résultats IA Interdits</h3>
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <p className="font-semibold mb-2">Interdiction Stricte de :</p>
                          <ul className="list-disc pl-6">
                            <li>Mise en relation discriminatoire basée sur des caractéristiques protégées</li>
                            <li>Tarification inéquitable basée sur des attributs personnels</li>
                            <li>Manipulation du comportement utilisateur à des fins commerciales</li>
                            <li>Profilage pour inférer des données personnelles sensibles</li>
                          </ul>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Section 4: Service Provisions */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <button
                onClick={() => toggleSection('service')}
                className="w-full p-6 text-left hover:bg-gray-50 transition-colors"
              >
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                    <Scale className="h-5 w-5 mr-2 text-primary-600" />
                    4. {isEnglish ? 'Service Provisions and Liability' : 'Dispositions de Service et Responsabilité'}
                  </h2>
                  {expandedSections.has('service') ? 
                    <ChevronUp className="h-5 w-5 text-gray-500" /> : 
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  }
                </div>
              </button>
              
              {expandedSections.has('service') && (
                <div className="px-6 pb-6 border-t border-gray-100">
                  <div className="prose max-w-none">
                    {isEnglish ? (
                      <>
                        <h3 className="text-lg font-semibold mb-3">4.1 Service Description</h3>
                        <p className="mb-4">
                          OpportunAI provides a digital marketplace connecting entrepreneurs with AI specialists. 
                          The service includes profile creation, project posting, proposal submission, and communication tools.
                        </p>

                        <h3 className="text-lg font-semibold mb-3">4.2 Liability Framework</h3>
                        
                        <h4 className="font-semibold mb-2">For Business Users (B2B):</h4>
                        <ul className="list-disc pl-6 mb-4">
                          <li>Service provided "as is" with limited warranties</li>
                          <li>Liability limited to direct damages up to 12 months of subscription fees</li>
                          <li>No liability for indirect, consequential, or business loss</li>
                          <li>User responsible for due diligence on service providers</li>
                        </ul>

                        <h4 className="font-semibold mb-2">For Consumers:</h4>
                        <ul className="list-disc pl-6 mb-4">
                          <li>Enhanced protections under Consumer Rights Directive</li>
                          <li>14-day withdrawal right for subscription services</li>
                          <li>Right to conforming service and remedies for non-conformity</li>
                          <li>No limitation of liability for personal injury or fraud</li>
                        </ul>

                        <h3 className="text-lg font-semibold mb-3">4.3 Security Breach Obligations</h3>
                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                          <p><strong>GDPR Compliance (Art. 33-34):</strong></p>
                          <ul className="list-disc pl-6 mt-2">
                            <li>Supervisory authority notification within 72 hours</li>
                            <li>User notification without undue delay if high risk</li>
                            <li>Detailed incident documentation and remediation</li>
                            <li>Regular security assessments and updates</li>
                          </ul>
                        </div>

                        <h3 className="text-lg font-semibold mb-3">4.4 Force Majeure</h3>
                        <p>
                          Neither party shall be liable for delays or failures due to circumstances beyond reasonable control, 
                          including but not limited to natural disasters, government actions, or technical infrastructure failures.
                        </p>
                      </>
                    ) : (
                      <>
                        <h3 className="text-lg font-semibold mb-3">4.1 Description du Service</h3>
                        <p className="mb-4">
                          OpportunAI fournit une place de marché numérique connectant entrepreneurs et spécialistes IA. 
                          Le service inclut la création de profils, publication de projets, soumission de propositions et outils de communication.
                        </p>

                        <h3 className="text-lg font-semibold mb-3">4.2 Cadre de Responsabilité</h3>
                        
                        <h4 className="font-semibold mb-2">Pour les Utilisateurs Professionnels (B2B) :</h4>
                        <ul className="list-disc pl-6 mb-4">
                          <li>Service fourni "en l'état" avec garanties limitées</li>
                          <li>Responsabilité limitée aux dommages directs jusqu'à 12 mois d'abonnement</li>
                          <li>Aucune responsabilité pour dommages indirects, consécutifs ou perte d'exploitation</li>
                          <li>Utilisateur responsable de la diligence raisonnable sur les prestataires</li>
                        </ul>

                        <h4 className="font-semibold mb-2">Pour les Consommateurs :</h4>
                        <ul className="list-disc pl-6 mb-4">
                          <li>Protections renforcées sous la Directive Droits des Consommateurs</li>
                          <li>Droit de rétractation de 14 jours pour les services d'abonnement</li>
                          <li>Droit à un service conforme et recours en cas de non-conformité</li>
                          <li>Aucune limitation de responsabilité pour blessures corporelles ou fraude</li>
                        </ul>

                        <h3 className="text-lg font-semibold mb-3">4.3 Obligations en cas de Violation de Sécurité</h3>
                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                          <p><strong>Conformité RGPD (Art. 33-34) :</strong></p>
                          <ul className="list-disc pl-6 mt-2">
                            <li>Notification à l'autorité de contrôle dans les 72 heures</li>
                            <li>Notification aux utilisateurs sans délai indu si risque élevé</li>
                            <li>Documentation détaillée de l'incident et remédiation</li>
                            <li>Évaluations et mises à jour de sécurité régulières</li>
                          </ul>
                        </div>

                        <h3 className="text-lg font-semibold mb-3">4.4 Force Majeure</h3>
                        <p>
                          Aucune partie ne sera responsable des retards ou défaillances dus à des circonstances hors de contrôle raisonnable, 
                          incluant mais non limité aux catastrophes naturelles, actions gouvernementales ou défaillances d'infrastructure technique.
                        </p>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Section 5: User Obligations */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <button
                onClick={() => toggleSection('obligations')}
                className="w-full p-6 text-left hover:bg-gray-50 transition-colors"
              >
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                    <AlertTriangle className="h-5 w-5 mr-2 text-primary-600" />
                    5. {isEnglish ? 'User Obligations and Prohibited Conduct' : 'Obligations Utilisateur et Conduites Interdites'}
                  </h2>
                  {expandedSections.has('obligations') ? 
                    <ChevronUp className="h-5 w-5 text-gray-500" /> : 
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  }
                </div>
              </button>
              
              {expandedSections.has('obligations') && (
                <div className="px-6 pb-6 border-t border-gray-100">
                  <div className="prose max-w-none">
                    {isEnglish ? (
                      <>
                        <h3 className="text-lg font-semibold mb-3">5.1 Account Responsibilities</h3>
                        <ul className="list-disc pl-6 mb-4">
                          <li>Provide accurate and complete registration information</li>
                          <li>Maintain confidentiality of account credentials</li>
                          <li>Promptly update profile information when changes occur</li>
                          <li>Use the platform only for legitimate business purposes</li>
                        </ul>

                        <h3 className="text-lg font-semibold mb-3">5.2 Prohibited Activities</h3>
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                          <ul className="list-disc pl-6">
                            <li>Posting false, misleading, or discriminatory content</li>
                            <li>Attempting to circumvent platform fees or payment systems</li>
                            <li>Harvesting user data for unauthorized purposes</li>
                            <li>Engaging in harassment, spam, or abusive behavior</li>
                            <li>Violating intellectual property rights</li>
                            <li>Using automated tools to manipulate platform metrics</li>
                          </ul>
                        </div>

                        <h3 className="text-lg font-semibold mb-3">5.3 Content Standards</h3>
                        <p className="mb-4">
                          All user-generated content must comply with:
                        </p>
                        <ul className="list-disc pl-6 mb-4">
                          <li>EU laws on hate speech and discrimination</li>
                          <li>Professional communication standards</li>
                          <li>Intellectual property regulations</li>
                          <li>Platform community guidelines</li>
                        </ul>

                        <h3 className="text-lg font-semibold mb-3">5.4 Enforcement</h3>
                        <p>
                          Violations may result in warnings, content removal, account suspension, or termination. 
                          Serious violations will be reported to relevant authorities.
                        </p>
                      </>
                    ) : (
                      <>
                        <h3 className="text-lg font-semibold mb-3">5.1 Responsabilités du Compte</h3>
                        <ul className="list-disc pl-6 mb-4">
                          <li>Fournir des informations d'inscription exactes et complètes</li>
                          <li>Maintenir la confidentialité des identifiants de compte</li>
                          <li>Mettre à jour rapidement les informations de profil en cas de changement</li>
                          <li>Utiliser la plateforme uniquement à des fins commerciales légitimes</li>
                        </ul>

                        <h3 className="text-lg font-semibold mb-3">5.2 Activités Interdites</h3>
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                          <ul className="list-disc pl-6">
                            <li>Publication de contenu faux, trompeur ou discriminatoire</li>
                            <li>Tentative de contournement des frais ou systèmes de paiement de la plateforme</li>
                            <li>Collecte de données utilisateur à des fins non autorisées</li>
                            <li>Harcèlement, spam ou comportement abusif</li>
                            <li>Violation des droits de propriété intellectuelle</li>
                            <li>Utilisation d'outils automatisés pour manipuler les métriques de la plateforme</li>
                          </ul>
                        </div>

                        <h3 className="text-lg font-semibold mb-3">5.3 Standards de Contenu</h3>
                        <p className="mb-4">
                          Tout contenu généré par l'utilisateur doit respecter :
                        </p>
                        <ul className="list-disc pl-6 mb-4">
                          <li>Les lois UE sur les discours de haine et la discrimination</li>
                          <li>Les standards de communication professionnelle</li>
                          <li>Les réglementations de propriété intellectuelle</li>
                          <li>Les directives communautaires de la plateforme</li>
                        </ul>

                        <h3 className="text-lg font-semibold mb-3">5.4 Application</h3>
                        <p>
                          Les violations peuvent entraîner des avertissements, suppression de contenu, suspension ou résiliation de compte. 
                          Les violations graves seront signalées aux autorités compétentes.
                        </p>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Section 6: Termination */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <button
                onClick={() => toggleSection('termination')}
                className="w-full p-6 text-left hover:bg-gray-50 transition-colors"
              >
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                    <ExternalLink className="h-5 w-5 mr-2 text-primary-600" />
                    6. {isEnglish ? 'Termination and Data Deletion' : 'Résiliation et Suppression des Données'}
                  </h2>
                  {expandedSections.has('termination') ? 
                    <ChevronUp className="h-5 w-5 text-gray-500" /> : 
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  }
                </div>
              </button>
              
              {expandedSections.has('termination') && (
                <div className="px-6 pb-6 border-t border-gray-100">
                  <div className="prose max-w-none">
                    {isEnglish ? (
                      <>
                        <h3 className="text-lg font-semibold mb-3">6.1 Voluntary Termination</h3>
                        <p className="mb-4">
                          You may terminate your account at any time through your account settings or by contacting support.
                        </p>

                        <h3 className="text-lg font-semibold mb-3">6.2 Data Deletion Process</h3>
                        <ul className="list-disc pl-6 mb-4">
                          <li><strong>Immediate:</strong> Profile visibility removed, new interactions disabled</li>
                          <li><strong>30 days:</strong> Personal data anonymized or deleted</li>
                          <li><strong>Exceptions:</strong> Legal obligations may require longer retention</li>
                          <li><strong>Backups:</strong> Removed from next backup cycle (max 90 days)</li>
                        </ul>

                        <h3 className="text-lg font-semibold mb-3">6.3 Involuntary Termination</h3>
                        <p className="mb-4">
                          We may suspend or terminate accounts for violations of these Terms, with notice when legally required.
                        </p>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <p><strong>Data Portability Before Termination:</strong></p>
                          <p>Request your data export before account deletion: contact@opportunai.com</p>
                        </div>
                      </>
                    ) : (
                      <>
                        <h3 className="text-lg font-semibold mb-3">6.1 Résiliation Volontaire</h3>
                        <p className="mb-4">
                          Vous pouvez résilier votre compte à tout moment via les paramètres de compte ou en contactant le support.
                        </p>

                        <h3 className="text-lg font-semibold mb-3">6.2 Processus de Suppression des Données</h3>
                        <ul className="list-disc pl-6 mb-4">
                          <li><strong>Immédiat :</strong> Visibilité du profil supprimée, nouvelles interactions désactivées</li>
                          <li><strong>30 jours :</strong> Données personnelles anonymisées ou supprimées</li>
                          <li><strong>Exceptions :</strong> Les obligations légales peuvent nécessiter une conservation plus longue</li>
                          <li><strong>Sauvegardes :</strong> Supprimées du prochain cycle de sauvegarde (max 90 jours)</li>
                        </ul>

                        <h3 className="text-lg font-semibold mb-3">6.3 Résiliation Involontaire</h3>
                        <p className="mb-4">
                          Nous pouvons suspendre ou résilier des comptes pour violations de ces CGU, avec préavis quand légalement requis.
                        </p>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <p><strong>Portabilité des Données Avant Résiliation :</strong></p>
                          <p>Demandez l'export de vos données avant suppression de compte : export@opportunai.com</p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Sidebar - Plain Language Summaries */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              {/* Quick Summary */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <Eye className="h-5 w-5 mr-2 text-primary-600" />
                  {isEnglish ? 'Quick Summary' : 'Résumé Rapide'}
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start space-x-2">
                    <div className="w-2 h-2 bg-primary-600 rounded-full mt-2 flex-shrink-0"></div>
                    <p>
                      {isEnglish 
                        ? 'We connect entrepreneurs with AI specialists through our platform'
                        : 'Nous connectons entrepreneurs et spécialistes IA via notre plateforme'
                      }
                    </p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <div className="w-2 h-2 bg-primary-600 rounded-full mt-2 flex-shrink-0"></div>
                    <p>
                      {isEnglish 
                        ? 'Your data is protected under GDPR with full user rights'
                        : 'Vos données sont protégées sous RGPD avec droits utilisateur complets'
                      }
                    </p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <div className="w-2 h-2 bg-primary-600 rounded-full mt-2 flex-shrink-0"></div>
                    <p>
                      {isEnglish 
                        ? 'AI decisions can be reviewed by humans upon request'
                        : 'Les décisions IA peuvent être révisées par des humains sur demande'
                      }
                    </p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <div className="w-2 h-2 bg-primary-600 rounded-full mt-2 flex-shrink-0"></div>
                    <p>
                      {isEnglish 
                        ? 'You can delete your account and data at any time'
                        : 'Vous pouvez supprimer votre compte et données à tout moment'
                      }
                    </p>
                  </div>
                </div>
              </div>

              {/* Key Contacts */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-lg font-semibold mb-4">
                  {isEnglish ? 'Key Contacts' : 'Contacts Clés'}
                </h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <p className="font-medium">
                      {isEnglish ? 'Data Protection Officer' : 'Délégué à la Protection des Données'}
                    </p>
                    <p className="text-gray-600">contact@opportunai.com</p>
                  </div>
                  <div>
                    <p className="font-medium">
                      {isEnglish ? 'AI Review Team' : 'Équipe Révision IA'}
                    </p>
                    <p className="text-gray-600">contact@opportunai.com</p>
                  </div>
                  <div>
                    <p className="font-medium">
                      {isEnglish ? 'Legal Department' : 'Service Juridique'}
                    </p>
                    <p className="text-gray-600">contact@opportunai.com</p>
                  </div>
                  <div>
                    <p className="font-medium">
                      {isEnglish ? 'Supervisory Authority' : 'Autorité de Contrôle'}
                    </p>
                    <p className="text-gray-600">CNIL - www.cnil.fr</p>
                  </div>
                </div>
              </div>

              {/* Legal Definitions */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-lg font-semibold mb-4">
                  {isEnglish ? 'Legal Definitions' : 'Définitions Juridiques'}
                </h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <button
                      className="font-medium text-primary-600 hover:text-primary-700"
                      onMouseEnter={() => setShowTooltip('gdpr')}
                      onMouseLeave={() => setShowTooltip(null)}
                    >
                      GDPR
                    </button>
                    {showTooltip === 'gdpr' && (
                      <div className="mt-1 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                        {isEnglish 
                          ? 'General Data Protection Regulation - EU law on data protection and privacy'
                          : 'Règlement Général sur la Protection des Données - Loi UE sur protection et confidentialité des données'
                        }
                      </div>
                    )}
                  </div>
                  <div>
                    <button
                      className="font-medium text-primary-600 hover:text-primary-700"
                      onMouseEnter={() => setShowTooltip('controller')}
                      onMouseLeave={() => setShowTooltip(null)}
                    >
                      {isEnglish ? 'Data Controller' : 'Responsable de Traitement'}
                    </button>
                    {showTooltip === 'controller' && (
                      <div className="mt-1 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                        {isEnglish 
                          ? 'Entity that determines purposes and means of personal data processing'
                          : 'Entité qui détermine les finalités et moyens du traitement des données personnelles'
                        }
                      </div>
                    )}
                  </div>
                  <div>
                    <button
                      className="font-medium text-primary-600 hover:text-primary-700"
                      onMouseEnter={() => setShowTooltip('automated')}
                      onMouseLeave={() => setShowTooltip(null)}
                    >
                      {isEnglish ? 'Automated Decision' : 'Décision Automatisée'}
                    </button>
                    {showTooltip === 'automated' && (
                      <div className="mt-1 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                        {isEnglish 
                          ? 'Decision made solely by automated means without human intervention'
                          : 'Décision prise uniquement par des moyens automatisés sans intervention humaine'
                        }
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Acceptance Section */}
        <div className="bg-white rounded-lg shadow-lg p-8 mt-8">
          <div className="text-center">
            <h2 className="text-2xl font-semibold mb-6">
              {isEnglish ? 'Terms Acceptance' : 'Acceptation des Conditions'}
            </h2>
            
            {!hasReadSufficiently && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mx-auto mb-2" />
                <p className="text-yellow-800">
                  {isEnglish 
                    ? 'Please read at least 90% of the terms before accepting'
                    : 'Veuillez lire au moins 90% des conditions avant d\'accepter'
                  }
                </p>
                <p className="text-sm text-yellow-600 mt-1">
                  {isEnglish ? 'Progress' : 'Progression'}: {Math.round(scrollProgress)}%
                </p>
              </div>
            )}

            {sessionTimeout && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <AlertTriangle className="h-5 w-5 text-red-600 mx-auto mb-2" />
                <p className="text-red-800">
                  {isEnglish 
                    ? 'Session expired. Please refresh the page to continue.'
                    : 'Session expirée. Veuillez actualiser la page pour continuer.'
                  }
                </p>
              </div>
            )}

            <div className="flex items-center justify-center space-x-4 mb-6">
              <input
                type="checkbox"
                id="terms-acceptance"
                checked={hasAccepted}
                onChange={handleAcceptance}
                disabled={!hasReadSufficiently || sessionTimeout}
                className="h-5 w-5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50"
              />
              <label htmlFor="terms-acceptance" className="text-lg">
                {isEnglish 
                  ? 'I have read and accept the Terms of Service'
                  : 'J\'ai lu et j\'accepte les Conditions Générales d\'Utilisation'
                }
              </label>
            </div>

            {hasAccepted && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <CheckCircle className="h-6 w-6 text-green-600 mx-auto mb-2" />
                <p className="text-green-800 font-medium">
                  {isEnglish 
                    ? 'Terms accepted successfully!'
                    : 'Conditions acceptées avec succès !'
                  }
                </p>
                <p className="text-sm text-green-600 mt-1">
                  {isEnglish ? 'Timestamp' : 'Horodatage'}: {new Date().toLocaleString()}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer Links */}
        <div className="text-center mt-8 space-x-6 text-sm text-gray-500">
          <a href="/privacy-policy" className="hover:text-primary-600 transition-colors">
            {isEnglish ? 'Privacy Policy' : 'Politique de Confidentialité'}
          </a>
          <a href="/cookie-policy" className="hover:text-primary-600 transition-colors">
            {isEnglish ? 'Cookie Policy' : 'Politique des Cookies'}
          </a>
          <a href="/contact" className="hover:text-primary-600 transition-colors">
            {isEnglish ? 'Contact Us' : 'Nous Contacter'}<p className="text-gray-600">contact@opportunai.com</p>
          </a>
        </div>
      </div>

      {/* Schema.org Markup */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "TermsOfService",
            "name": isEnglish ? "OpportunAI Terms of Service" : "Conditions Générales OpportunAI",
            "url": "https://opportunai.com/terms-of-service",
            "dateModified": "2025-01-01",
            "publisher": {
              "@type": "Organization",
              "name": "KphApps",
              "url": "https://opportunai.com"
            },
            "jurisdiction": "EU",
            "governingLaw": "French Law and EU Regulations",
            "applicableRegulation": [
              "GDPR 2016/679",
              "E-Commerce Directive 2000/31/EC",
              "Consumer Rights Directive 2011/83/EU"
            ]
          })
        }}
      />

      {/* Accept / Decline sticky bar */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg px-4 py-3 flex flex-col sm:flex-row gap-3 sm:justify-center sm:items-center print:hidden">
        <button
          onClick={handleAccept}
          className="w-full sm:w-auto bg-primary-600 hover:bg-primary-700 text-white font-medium px-8 py-3 rounded-lg shadow transition-colors text-base"
        >
          {isEnglish ? 'I Accept' : "J'accepte"}
        </button>
        <button
          onClick={handleDecline}
          className="w-full sm:w-auto border-2 border-gray-400 text-gray-600 hover:border-gray-500 hover:text-gray-700 font-medium px-8 py-3 rounded-lg transition-colors text-base bg-white"
        >
          {isEnglish ? 'Decline' : 'Refuser'}
        </button>
      </div>

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          .fixed { position: static !important; }
          .shadow-lg { box-shadow: none !important; }
          .bg-gradient-to-r { background: white !important; color: black !important; }
          .hover\\:bg-gray-50:hover { background: white !important; }
          button { display: none !important; }
          .expanded-section { display: block !important; }
          
          @page {
            margin: 2cm;
            @bottom-center {
              content: "Page " counter(page) " of " counter(pages);
            }
          }
          
          h1, h2 { page-break-after: avoid; }
          .section { page-break-inside: avoid; }
        }
      `}</style>
    </div>
  );
};

export default TermsOfService;